import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ROUTE_IDS } from "./routeTable";
import type { UIEvent } from "../lib/events-adapter";

/**
 * Shell router — URL-synced SPA router (History API). The browser URL is the
 * single source of truth: boot derives the route from window.location, every
 * nav writes the URL (pushState/replaceState), and popstate (back/forward,
 * deep-link, refresh) maps the URL back to a route id.
 *
 *   const nav = useNav();
 *   nav.go(route, params?)   — push a route   (history.pushState)
 *   nav.back()               — browser back   (history.back)
 *   nav.replace(route, p?)   — swap the top    (history.replaceState)
 *   nav.route / nav.params   — current route id + params
 *
 * routeId ↔ path is a 1:1 dots→slashes map: `create.type ↔ /create/type`,
 * `dashboard ↔ /dashboard`. Params serialize to the query string. The app is
 * served at the tenant root, so paths are absolute root-relative.
 *
 * Attend-host exception (2026-07-14 docket): on `attend.<tenant>` a BARE-slug
 * path — exactly what publicUrl()/openPublicEventPage() emit — resolves to
 * `event.public` with { slug }, and routeToPath emits the same bare-slug form
 * back so the public WYSIWYG URL survives boot-normalize and in-app navs.
 * `/event/public?slug=…` keeps working everywhere, unchanged.
 *
 * CD-owned shell code. Screens are resolved by routeTable.ts + <AppRouter/>.
 * All history.* calls are wrapped in try/catch so a sandboxed/opaque-origin
 * preview (where pushState throws) degrades gracefully to in-memory routing.
 */
export interface NavEntry {
  route: string;
  params: Record<string, unknown>;
}

export interface NavApi {
  route: string;
  params: Record<string, unknown>;
  canBack: boolean;
  /** Alias of canBack (prototype name). */
  canGoBack: boolean;
  /** The route one level down the stack, or null. */
  prevRoute: string | null;
  go: (route: string, params?: Record<string, unknown>) => void;
  back: () => void;
  replace: (route: string, params?: Record<string, unknown>) => void;
  /** Replace the entire stack (e.g. sign-out → signin). */
  reset: (route: string, params?: Record<string, unknown>) => void;
  /** Open the event detail drawer over the current screen. */
  openEvent: (ev: UIEvent) => void;
  /** Close the event detail drawer. */
  closeEvent: () => void;
  /** The event whose detail drawer is open, or null. */
  drawerEvent: UIEvent | null;
}

const NavContext = createContext<NavApi | null>(null);

export function useNav(): NavApi {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error("useNav() must be used inside <NavProvider>");
  return ctx;
}

const ROUTE_SET: ReadonlySet<string> = new Set(ROUTE_IDS as readonly string[]);

// ── Anonymous attend host (2026-07-14 docket) ───────────────────────────────
// attend.<tenant> serves the published event landing/registration experience
// to ANONYMOUS visitors; studio.<tenant> stays SSO-walled. Same SPA build —
// the host is the switch.
/** Route ids an anonymous attend-host visitor may reach. Matches the gateway's
 *  anonymous allowlist (published-landing read + registration submit); extend
 *  only when the corresponding anonymous data path is live. */
export const PUBLIC_ATTEND_ROUTES: ReadonlySet<string> = new Set(["event.public", "event.register"]);

/** True when the app is served from the public attend host. Host-based per the
 *  docket (`attend.` subdomain / bare `attend` label). `__ATTEND_HOST__` is a
 *  window override so the CD preview harness (which can't fake a hostname) can
 *  exercise the anonymous flow. */ // ui-config
export function isAttendHost(): boolean {
  if (typeof window === "undefined") return false;
  if ((window as unknown as { __ATTEND_HOST__?: boolean }).__ATTEND_HOST__ === true) return true;
  const h = (window.location.hostname || "").toLowerCase();
  return h === "attend" || h.startsWith("attend.");
}

/** Anonymous-OK: a public event route requested on the attend host. Studio-host
 *  behavior (and every operator route on any host) is untouched. */
export function isPublicAttendRoute(route: string): boolean {
  return isAttendHost() && PUBLIC_ATTEND_ROUTES.has(route);
}

/** Bare-slug path segment (what slugify() emits): single segment, no slashes. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/i;

/** routeId + params → root-relative path (+ query). `create.type` → `/create/type`. */
export function routeToPath(route: string, params: Record<string, unknown> = {}): string {
  // Attend host: event.public keeps the WYSIWYG bare-slug URL (attend.<tenant>/<slug>)
  // instead of /event/public?slug=… — boot-normalize and in-app navs must not
  // rewrite the link the visitor arrived on.
  if (route === "event.public" && typeof params.slug === "string" && SLUG_RE.test(params.slug) && isAttendHost()) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (k === "slug" || v === null || v === undefined || v === "") continue;
      qs.set(k, String(v));
    }
    const q = qs.toString();
    return q ? `/${params.slug}?${q}` : `/${params.slug}`;
  }
  const path = "/" + route.split(".").join("/");
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    qs.set(k, String(v));
  }
  const q = qs.toString();
  return q ? `${path}?${q}` : path;
}

/** pathname (+ search) → routeId + params. `/create/type` → `create.type`. */
// #8 (2026-07-14 docket) — index redirects: a bare section path canonicalizes to
// its default sub-view instead of falling through to the dashboard fallback
// (typed/bookmarked `/calendar` deep-links bounced to /dashboard; the nav's own
// links already target /calendar/week).
const ROUTE_INDEX_REDIRECTS: Record<string, string> = { calendar: "calendar.week", workforce: "workforce.schedule", storeops: "storeops.floor" };
function pathToEntry(pathname: string, search: string): NavEntry {
  const clean = pathname.replace(/^\/+|\/+$/g, "");
  const raw = clean ? clean.split("/").join(".") : "dashboard";
  const route = ROUTE_INDEX_REDIRECTS[raw] || raw;
  const params: Record<string, unknown> = {};
  new URLSearchParams(search).forEach((v, k) => { params[k] = v; });
  // Attend host: an unknown single-segment path is a public-event slug —
  // attend.<tenant>/<slug> → event.public { slug } → <PublicEventLanding/> →
  // getPublishedLandingSurface(slug). Known route ids win (so /event/public
  // resolution is unchanged, and operator deep-links behave identically).
  if (!ROUTE_SET.has(route) && isAttendHost() && SLUG_RE.test(clean)) {
    return { route: "event.public", params: { ...params, slug: clean } };
  }
  return { route, params };
}

export function NavProvider({ initialRoute = "dashboard", initialParams = {}, children }: {
  initialRoute?: string;
  initialParams?: Record<string, unknown>;
  children: ReactNode;
}) {
  const parseLoc = useCallback((): NavEntry => {
    if (typeof window === "undefined") return { route: initialRoute, params: initialParams };
    const entry = pathToEntry(window.location.pathname, window.location.search);
    // Unknown path (e.g. preview harness served at "/index.html") → boot fallback.
    return ROUTE_SET.has(entry.route) ? entry : { route: initialRoute, params: initialParams };
  }, [initialRoute, initialParams]);

  const stackRef = useRef<NavEntry[]>([]);
  const idxRef = useRef(0);

  const [current, setCurrent] = useState<NavEntry>(() => {
    const e = parseLoc();
    stackRef.current = [e];
    idxRef.current = 0;
    // Normalize the URL on boot so state.i exists for popstate bookkeeping.
    try { window.history.replaceState({ i: 0 }, "", routeToPath(e.route, e.params)); } catch { /* opaque origin */ }
    return e;
  });

  const go = useCallback((route: string, params: Record<string, unknown> = {}) => {
    const entry = { route, params };
    const ni = idxRef.current + 1;
    stackRef.current = [...stackRef.current.slice(0, idxRef.current + 1), entry];
    idxRef.current = ni;
    try { window.history.pushState({ i: ni }, "", routeToPath(route, params)); } catch { /* opaque origin */ }
    setCurrent(entry);
  }, []);

  const replace = useCallback((route: string, params: Record<string, unknown> = {}) => {
    const entry = { route, params };
    const next = [...stackRef.current];
    next[idxRef.current] = entry;
    stackRef.current = next;
    try { window.history.replaceState({ i: idxRef.current }, "", routeToPath(route, params)); } catch { /* opaque origin */ }
    setCurrent(entry);
  }, []);

  const reset = useCallback((route: string, params: Record<string, unknown> = {}) => {
    const entry = { route, params };
    stackRef.current = [entry];
    idxRef.current = 0;
    try { window.history.replaceState({ i: 0 }, "", routeToPath(route, params)); } catch { /* opaque origin */ }
    setCurrent(entry);
  }, []);

  const back = useCallback(() => {
    try {
      window.history.back();
    } catch {
      if (idxRef.current > 0) { idxRef.current -= 1; setCurrent(stackRef.current[idxRef.current]); }
    }
  }, []);

  // ── Event detail drawer (overlay; rides the URL as ?detail=<id> for share) ──
  const [drawerEvent, setDrawerEvent] = useState<UIEvent | null>(null);
  const openEvent = useCallback((ev: UIEvent) => {
    setDrawerEvent(ev);
    // Reflect the open drawer in the URL (no new history entry) so the link is
    // shareable; closing/navigating clears it. Deep-link rehydration (fetch by
    // id on boot) is a follow-on — the in-memory object covers the live click.
    try {
      const sp = new URLSearchParams(window.location.search);
      if (ev?.id) { sp.set("detail", ev.id); window.history.replaceState(window.history.state, "", window.location.pathname + "?" + sp.toString()); }
    } catch { /* opaque origin */ }
  }, []);
  const closeEvent = useCallback(() => {
    setDrawerEvent(null);
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.has("detail")) { sp.delete("detail"); const q = sp.toString(); window.history.replaceState(window.history.state, "", window.location.pathname + (q ? "?" + q : "")); }
    } catch { /* opaque origin */ }
  }, []);

  // Any real navigation (go/replace/reset change `current`) closes the drawer,
  // matching the prototype. openEvent never changes `current`, so the drawer
  // it just opened survives this effect.
  useEffect(() => { setDrawerEvent(null); }, [current.route, current.params]);

  // Browser back/forward (and deep-link/refresh via the boot replaceState).
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const st = e.state as { i?: number } | null;
      if (st && typeof st.i === "number" && stackRef.current[st.i]) {
        idxRef.current = st.i;
        setCurrent(stackRef.current[st.i]);
      } else {
        // External/deep history change — re-parse the URL as the truth.
        const entry = parseLoc();
        const found = stackRef.current.findIndex((s) => s.route === entry.route);
        if (found >= 0) idxRef.current = found;
        setCurrent(entry);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [parseLoc]);

  const value = useMemo<NavApi>(() => ({
    route: current.route,
    params: current.params,
    canBack: idxRef.current > 0,
    canGoBack: idxRef.current > 0,
    prevRoute: idxRef.current > 0 ? stackRef.current[idxRef.current - 1]?.route ?? null : null,
    go, back, replace, reset, openEvent, closeEvent, drawerEvent,
  }), [current, go, back, replace, reset, openEvent, closeEvent, drawerEvent]);

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}
